import { EditPromotionChannelDialog } from "@/app/Promotions/Components/EditPromotionChannelDialog";
import { PlatformCell } from "@/app/Promotions/Components/PlatformCell";
import { PromotionListChannel } from "@/app/Promotions/GraphQL/relocationPromotionListQuery";
import { syncPromotionChannelMutation } from "@/app/Promotions/GraphQL/promotionChannelMutations";
import { promotionStatusColorMap } from "@/app/Promotions/Utils/promotionMaps";
import { Badge } from "@/components/catalyst/badge";
import { Button } from "@/components/catalyst/button";
import { Currency } from "@/gql/graphql";
import { TimeSinceNow } from "@/lib/Components/Common/TimeSinceNow";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { PencilIcon } from "@heroicons/react/24/outline";
import { RefreshCwIcon } from "lucide-react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

const numberFormatter = new Intl.NumberFormat("en-US");

/**
 * The platform breakdown behind a promotion's row — each channel is where the
 * budget, status and metrics actually live; the row above is only their sum.
 */
export function PromotionChannelSubTable({
  channels,
}: {
  channels: readonly PromotionListChannel[];
}) {
  const t = useTranslations("promotion.table");

  return (
    <div className="overflow-x-auto px-4 py-3">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
            <th className="py-2 pr-6 font-medium">{t("platform")}</th>
            <th className="py-2 pr-6 font-medium">{t("status")}</th>
            <th className="py-2 pr-6 font-medium">{t("budget")}</th>
            <th className="py-2 pr-6 font-medium">{t("impressions")}</th>
            <th className="py-2 pr-6 font-medium">{t("clicks")}</th>
            <th className="py-2 pr-6 font-medium">{t("conversions")}</th>
            <th className="py-2 pr-6 font-medium">{t("spend")}</th>
            <th className="py-2 pr-6 font-medium">{t("lastSynced")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {channels.map((channel) => (
            <ChannelRow key={channel.id} channel={channel} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChannelRow({ channel }: { channel: PromotionListChannel }) {
  const t = useTranslations("promotion.actions");
  const { open: openEditDialog } = useDialog(EditPromotionChannelDialog);
  const { mutateAsync: syncChannel, isPending } = useGqlMutation(
    syncPromotionChannelMutation,
  );

  return (
    <tr className="border-t border-gray-200">
      <td className="whitespace-nowrap py-2 pr-6">
        <PlatformCell
          platform={channel.platform}
          managerUrl={channel.manager_url}
        />
      </td>
      <td className="whitespace-nowrap py-2 pr-6">
        <Badge color={promotionStatusColorMap[channel.status]}>
          {channel.status}
        </Badge>
      </td>
      <td className="whitespace-nowrap py-2 pr-6">
        {formatCurrency(channel.budget_amount, Currency.Usd, {
          showZero: true,
        })}
      </td>
      <td className="whitespace-nowrap py-2 pr-6">
        {numberFormatter.format(channel.impressions)}
      </td>
      <td className="whitespace-nowrap py-2 pr-6">
        {numberFormatter.format(channel.clicks)}
      </td>
      <td className="whitespace-nowrap py-2 pr-6">
        {numberFormatter.format(channel.conversions)}
      </td>
      <td className="whitespace-nowrap py-2 pr-6">
        {formatCurrency(channel.spend_amount, Currency.Usd, { showZero: true })}
      </td>
      <td className="whitespace-nowrap py-2 pr-6 text-gray-500">
        {channel.last_synced_at ? (
          <TimeSinceNow time={channel.last_synced_at} />
        ) : (
          "—"
        )}
      </td>
      <td className="whitespace-nowrap py-2 text-right">
        <div className="flex justify-end gap-1">
          <Button
            plain
            LeadingIcon={PencilIcon}
            aria-label={t("edit")}
            title={t("edit")}
            onClick={() => {
              openEditDialog({
                channelId: channel.id,
                budgetAmount: channel.budget_amount,
                status: channel.status,
              });
            }}
          />

          <Button
            plain
            LeadingIcon={RefreshCwIcon}
            isLoading={isPending}
            aria-label={t("resync")}
            title={t("resync")}
            onClick={async () => {
              await syncChannel({ id: channel.id });

              toast.success(t("resyncSuccess"));
            }}
          />
        </div>
      </td>
    </tr>
  );
}
